import React, { useEffect, useState } from 'react';
import { SubscriptionResponse } from '@Types/newsletter/SubscriptionResponse';
import { useAccount, useNewsletter } from '@Provider/frontastic';
import { BikeAlarmButton } from 'components/commercetools-ui/account/details';
import ChevronLeftIcon from 'components/icons/chevron-left';
import Button from 'components/revelo-ui/content/button';
import { useNotifications } from '../../../../../frontastic/actions/account';
import { useFormat } from '../../../../../helpers/hooks/useFormat';
import Markdown from '../../../content/markdown';
import DeleteConfirmationModal from '../modals/delete-confirmation';
import Newsletter from '../notification/newsletter';
import NotificationItem from '../notification/notificationItem';

export type NotificationProps = {
  noBikeAlarmMarkdown?: string;
  noBikeAlarmButtons?: BikeAlarmButton[];
  unsubscribeAllMarkdown?: string;
  newsletterText?: string;
  newsletterDisclaimerText?: string;
  listId?: string;
  permissionId?: string;
  newsletterSuccessMessage?: string;
  onCloseContent?: (data?: any) => void;
};

const Notifications: React.FC<NotificationProps> = ({
  noBikeAlarmMarkdown,
  noBikeAlarmButtons,
  newsletterDisclaimerText,
  newsletterText,
  unsubscribeAllMarkdown,
  listId,
  permissionId,
  newsletterSuccessMessage,
  onCloseContent,
}) => {
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const { notifications, email } = useNotifications();
  const { deleteAllNotifications, account } = useAccount();
  const { getNewsletterInfo } = useNewsletter();
  const [newsletterStatus, setNewsletterStatus] = useState<SubscriptionResponse>({
    success: true,
    newsletterStatus: {
      permissionId: permissionId,
      enabled: true,
    },
  });

  const deleteAndUnsubscribe = async () => {
    await deleteAllNotifications();
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const openDeleteModal = () => setDeleteModalOpen(true);

  const closeDeleteModal = () => setDeleteModalOpen(false);

  useEffect(() => {
    const getData = async () => {
      const subscriptionResponse = await getNewsletterInfo({
        listId: listId,
        account,
        permissionId: permissionId,
      });
      setNewsletterStatus(subscriptionResponse);
    };

    getData();
  }, [getNewsletterInfo]);

  const splitTextLink = (text: string) => {
    const renderSpecial = (content: string) => {
      return (
        <button className="cursor-pointer text-secondary-600 underline" onClick={openDeleteModal}>
          {content}
        </button>
      );
    };
    const parts = text.split(/(%[^%]+%)/);

    return (
      <div className="text-sm">
        {parts.map((part, index) => {
          if (part.startsWith('%') && part.endsWith('%')) {
            const innerContent = part.slice(1, -1);
            return <span key={index}>{renderSpecial(innerContent)}</span>;
          }
          return <span key={index}>{part}</span>;
        })}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <h2 className="mb-8 flex items-center gap-2 font-headline text-xl lg:text-2xl">
        <button onClick={() => onCloseContent?.()} className="lg:hidden">
          <ChevronLeftIcon className="size-6" />
        </button>
        Benachrichtigungen
      </h2>
      <h3 className="mb-2 font-headline text-lg">Meine Bike Alarms</h3>
      {newsletterStatus?.subscriptionStatus === 'pending' && notifications.length > 0 && (
        <div className="mb-2">
          {formatAccountMessage({
            id: 'notifications.subscription.status.pending',
          })}
        </div>
      )}
      {newsletterStatus?.subscriptionStatus === 'unsubscribed' && notifications.length > 0 && (
        <div className="mb-2">
          {formatAccountMessage({
            id: 'notifications.subscription.status.unsubscribed',
          })}
        </div>
      )}
      {!notifications || notifications.length === 0 ? (
        <div className="mb-8">
          <Markdown text={noBikeAlarmMarkdown} className="prose text-base" />
          <div className="mt-6 flex flex-col gap-4 lg:flex-row">
            {noBikeAlarmButtons?.map((button, index) => {
              return (
                <Button key={index} style="hollow" reference={button.noBikeAlarmLink} className="w-full lg:w-auto">
                  {button.noBikeAlarmText}
                </Button>
              );
            })}
          </div>
        </div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {notifications?.map((entry) => {
              return <NotificationItem key={entry.id} entry={entry} />;
            })}
          </div>
          {splitTextLink(unsubscribeAllMarkdown)}
          <DeleteConfirmationModal open={deleteModalOpen} onClose={closeDeleteModal} onConfirm={deleteAndUnsubscribe} />
        </>
      )}
      {!newsletterStatus?.newsletterStatus?.enabled && (
        <Newsletter
          permissionId={permissionId}
          listId={listId}
          newsletterText={newsletterText.replace('%EMAIL%', email)}
          newsletterDisclaimerText={newsletterDisclaimerText}
          newsletterSuccessMessage={newsletterSuccessMessage}
        />
      )}
    </div>
  );
};

export default Notifications;
